import React, { useCallback, useEffect, useState } from 'react';

// Team Assignments and Team Exclusions for one person, used in People > Edit Person > Availability and in
// My Portal > Availability. The caller supplies `api`:
//   load() -> { assignments, exclusions, teams }   (teams: [{ teamName, division }])
//   add(kind, teamName, division) / remove(kind, id)   kind: 'assignment' | 'exclusion'
const SECTIONS = [
  {
    kind: 'assignment',
    listKey: 'assignments',
    title: 'Team Assignments',
    help: 'Teams this person plays for. They are not rostered in a timeslot while one of these teams is playing.',
    empty: 'No teams assigned',
    chip: '#fce4ec'
  },
  {
    kind: 'exclusion',
    listKey: 'exclusions',
    title: 'Team Exclusions',
    help: "Teams whose games this person must not be rostered on. They can still be rostered on other games, even at the same time.",
    empty: 'No teams excluded',
    chip: '#fff3e0'
  }
];

export default function PersonTeamLists({ api, readOnly = false, teams: teamsProp }) {
  const [data, setData] = useState({ assignments: [], exclusions: [], teams: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picks, setPicks] = useState({ assignment: '', exclusion: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.load();
      setData({ assignments: result.assignments || [], exclusions: result.exclusions || [], teams: result.teams || [] });
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load teams');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const teams = teamsProp && teamsProp.length ? teamsProp : data.teams;

  async function add(kind) {
    const pick = picks[kind];
    if (!pick) { setError('Please select a team'); return; }
    const [division, ...nameParts] = pick.split('|||');
    setBusy(true);
    try {
      await api.add(kind, nameParts.join('|||'), division);
      setPicks((p) => ({ ...p, [kind]: '' }));
      await load();
    } catch (err) {
      setError(err.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function remove(kind, id) {
    setBusy(true);
    try {
      await api.remove(kind, id);
      await load();
    } catch (err) {
      setError(err.message || 'Could not remove');
    } finally {
      setBusy(false);
    }
  }

  const grouped = {};
  teams.forEach((t) => {
    if (!grouped[t.division]) grouped[t.division] = [];
    grouped[t.division].push(t.teamName);
  });

  if (loading) return <p style={{ color: '#666' }}>Loading teams...</p>;

  return (
    <div>
      {error && <p style={{ color: '#d32f2f', margin: '0 0 0.75rem' }} role="alert">{error}</p>}
      {SECTIONS.map((section) => {
        const list = data[section.listKey];
        return (
          <div key={section.kind} style={{ marginTop: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.25rem' }}>{section.title}</h4>
            <p style={{ margin: '0 0 0.6rem', color: '#666', fontSize: '0.8rem' }}>{section.help}</p>
            <div style={{ marginBottom: '0.75rem' }}>
              {list.length === 0 ? (
                <p style={{ color: '#666', margin: 0 }}>{section.empty}</p>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {list.map((item) => (
                    <div key={item.id} style={{ backgroundColor: section.chip, padding: '0.5rem 1rem', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{item.division} - {item.teamName}{item.source === 'BC' ? <small style={{ color: '#777' }} title="Added automatically from the team they are registered in on BasketballConnect"> · from BasketballConnect</small> : null}</span>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => remove(section.kind, item.id)}
                          disabled={busy}
                          aria-label={`Remove ${item.teamName}`}
                          style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!readOnly && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  value={picks[section.kind]}
                  onChange={(e) => setPicks((p) => ({ ...p, [section.kind]: e.target.value }))}
                  style={{ flex: 1 }}
                  aria-label={`${section.title}: team to add`}
                >
                  <option value="">Select Team to Add</option>
                  {Object.keys(grouped).sort().map((div) => (
                    <optgroup key={div} label={div}>
                      {grouped[div].sort().map((name) => (
                        <option key={`${div}|||${name}`} value={`${div}|||${name}`}>{name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <button type="button" onClick={() => add(section.kind)} disabled={busy} className="btn-submit" style={{ padding: '0.5rem 1rem' }}>
                  Add
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
